/**
 * Date arithmetic for the delivery calendar, over the options the client
 * system actually offered.
 *
 * The one rule that shapes everything here: an offered date is an **opaque
 * token**. The server validates the selection against a freshly computed list
 * with strict string equality, so `"2026-08-12"` parsed into a `Date` and
 * re-serialised as `"2026-08-12T00:00:00.000Z"` is a different — rejected —
 * value for the same instant. Tokens go into cells and labels come out;
 * nothing in this module ever builds a new token.
 */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})/

interface DateParts {
  year: number
  /** 0-based, as `Date` counts them. */
  monthIndex: number
  day: number
}

/**
 * The calendar parts of a token, or null for one this calendar cannot draw.
 *
 * Reads the leading `yyyy-mm-dd` and ignores any time suffix — the client
 * system decides the token's exact shape, and a date-time still names a day.
 * Never `new Date(token)`: west of Greenwich that lands local rendering on
 * the previous day.
 */
export const datePartsOf = (token: string): DateParts | null => {
  const match = ISO_DAY.exec(token)
  if (!match) return null
  const [, year, month, day] = match
  const parts = {
    year: Number(year),
    monthIndex: Number(month) - 1,
    day: Number(day),
  }
  // `2026-13-45` matches the pattern; Date.UTC would happily roll it over
  // into a different month, which is a lie about what was offered.
  const check = new Date(Date.UTC(parts.year, parts.monthIndex, parts.day))
  const real =
    check.getUTCFullYear() === parts.year &&
    check.getUTCMonth() === parts.monthIndex &&
    check.getUTCDate() === parts.day
  return real ? parts : null
}

const WEEKDAY_LABELS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']
const MONTH_LABELS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
]

/** Monday-first, matching the design's `L M X J V S D` header. */
export const WEEKDAY_INITIALS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'] as const
export const WEEKDAY_NAMES = WEEKDAY_LABELS

/** ISO weekday, 1 = Monday … 7 = Sunday. UTC on purpose — see `datePartsOf`. */
const isoWeekdayOf = ({ year, monthIndex, day }: DateParts): number => {
  const weekday = new Date(Date.UTC(year, monthIndex, day)).getUTCDay()
  return weekday === 0 ? 7 : weekday
}

/**
 * `Martes 18 de agosto` — the label the design puts on a chosen date. Built
 * client-side because the response schema has no room for labels.
 */
export const deliveryOptionLabel = (token: string): string => {
  const parts = datePartsOf(token)
  if (!parts) return token
  const weekday = WEEKDAY_LABELS[isoWeekdayOf(parts) - 1]
  return `${weekday} ${parts.day} de ${MONTH_LABELS[parts.monthIndex]}`
}

export interface CalendarCell {
  /** `null` for the leading and trailing blanks that pad the month to weeks. */
  day: number | null
  /** The offered token this cell selects, `null` for unofferable days. */
  token: string | null
}

export interface CalendarMonth {
  /** `Agosto 2026` */
  label: string
  weeks: CalendarCell[][]
}

const daysIn = (year: number, monthIndex: number): number =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate()

/**
 * The offered dates as calendar months, Monday-first — one grid per month the
 * offer touches, in date order. A day is selectable exactly when the client
 * system offered it; every other cell is context.
 *
 * Malformed tokens are dropped rather than crashing the screen: the options
 * come from a per-tenant endpoint, and one bad row should cost that row, not
 * checkout.
 */
export const calendarMonths = (tokens: readonly string[]): CalendarMonth[] => {
  const tokensByDay = new Map<string, string>()
  for (const token of tokens) {
    const parts = datePartsOf(token)
    if (!parts) continue
    const key = `${parts.year}-${parts.monthIndex}-${parts.day}`
    // First offer wins; two tokens for one day would be the endpoint
    // disagreeing with itself.
    if (!tokensByDay.has(key)) tokensByDay.set(key, token)
  }

  const monthKeys = [...tokensByDay.keys()]
    .map((key) => key.split('-').map(Number) as [number, number, number])
    .sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2])

  const months: CalendarMonth[] = []
  const seen = new Set<string>()

  for (const [year, monthIndex] of monthKeys) {
    const monthKey = `${year}-${monthIndex}`
    if (seen.has(monthKey)) continue
    seen.add(monthKey)

    const cells: CalendarCell[] = []
    for (let blank = 1; blank < isoWeekdayOf({ year, monthIndex, day: 1 }); blank += 1) {
      cells.push({ day: null, token: null })
    }
    for (let day = 1; day <= daysIn(year, monthIndex); day += 1) {
      cells.push({ day, token: tokensByDay.get(`${year}-${monthIndex}-${day}`) ?? null })
    }
    while (cells.length % 7 !== 0) cells.push({ day: null, token: null })

    const weeks: CalendarCell[][] = []
    for (let start = 0; start < cells.length; start += 7) {
      weeks.push(cells.slice(start, start + 7))
    }

    const monthName = MONTH_LABELS[monthIndex] ?? ''
    months.push({
      label: `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${year}`,
      weeks,
    })
  }

  return months
}
